import React from "react";
import clsx from "clsx";
import { useIntl } from "react-intl";

import { DescriptionRunResultDTO } from "@app/api/generated";
import { LabelComponent } from "@app/core";

import { RUN_COUNTERS } from "@app/modules/product-description/run-counters";
import { formatDuration } from "@app/util/format-duration";
import { getPlatformName } from "@app/util/platform-name";
import { RUN_STATUS_LABEL_KEY } from "@app/util/run-status";
import { useProcessedDate } from "@app/util/use-processed-date";

import styles from "@app/styles/dashboard/run-detail-sidebar.module.scss";

interface IRunDetailSidebarProps {
  run: DescriptionRunResultDTO;
}

const RunDetailSidebar = ({ run }: IRunDetailSidebarProps): JSX.Element => {
  const intl = useIntl();
  const formatProcessedDate = useProcessedDate();

  /** Four decimals, in dollars: a run of 200 descriptions costs about nine cents. */
  const cost = intl.formatNumber(run.estimatedCostUsd ?? 0, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 4,
    maximumFractionDigits: 4
  });

  return (
    <aside className={styles.sidebar}>
      <dl className={styles.meta}>
        <div>
          <dt>{intl.formatMessage({ id: "productDescription.table.id" })}</dt>
          <dd>{run.runId}</dd>
        </div>
        <div>
          <dt>{intl.formatMessage({ id: "productDescription.table.status" })}</dt>
          <dd>
            <LabelComponent
              type={run.status.toLowerCase()}
              label={intl.formatMessage({ id: RUN_STATUS_LABEL_KEY[run.status] })}
            />
          </dd>
        </div>
        <div>
          <dt>{intl.formatMessage({ id: "productDescription.table.finishedAt" })}</dt>
          <dd>{run.finishedAt ? formatProcessedDate(new Date(run.finishedAt)) : "—"}</dd>
        </div>
        <div>
          <dt>{intl.formatMessage({ id: "productDescription.detail.duration" })}</dt>
          <dd>{formatDuration(run)}</dd>
        </div>
        <div>
          <dt>{intl.formatMessage({ id: "productDescription.table.platform" })}</dt>
          <dd>{getPlatformName(intl, run.platformId)}</dd>
        </div>
        <div>
          <dt>{intl.formatMessage({ id: "productDescription.detail.model" })}</dt>
          <dd>{run.model ?? "—"}</dd>
        </div>
        <div>
          <dt>{intl.formatMessage({ id: "productDescription.table.cost" })}</dt>
          <dd>{cost}</dd>
        </div>
      </dl>

      <dl className={clsx(styles.meta, styles.counters)}>
        {RUN_COUNTERS.map((counter) => (
          <div key={counter.key}>
            <dt className={styles.counterLabel}>{intl.formatMessage({ id: counter.labelId })}</dt>
            <dd className={clsx(counter.color === "red" && run[counter.key] > 0 && styles.errorValue)}>
              {run[counter.key]}
            </dd>
          </div>
        ))}
        <div>
          <dt className={styles.counterLabel}>{intl.formatMessage({ id: "productDescription.detail.tokens" })}</dt>
          <dd>{`${intl.formatNumber(run.tokensIn ?? 0)} / ${intl.formatNumber(run.tokensOut ?? 0)}`}</dd>
        </div>
      </dl>

      {run.errorMessage && (
        <div className={styles.errorBox}>
          <span className={styles.errorTitle}>
            {intl.formatMessage({ id: "productDescription.detail.errorMessage" })}
          </span>
          <p>{run.errorMessage}</p>
        </div>
      )}
    </aside>
  );
};

export { RunDetailSidebar };
